import * as React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import dayjs from 'dayjs';
import { useLanguage } from '../../contexts/LanguageContext';
import PageContainer from './PageContainer';
import DateRangeFilter from './DateRangeFilter';
import { getAllOrdersStatistics, type OrdersStatistics as OrdersStatisticsType } from '../data/orders';

export default function OrdersStatistics() {
  const { t, direction } = useLanguage();
  const isRtl = direction === 'rtl';

  // Set default dates to current month
  const getCurrentMonthDates = () => {
    const startOfMonth = dayjs().startOf('month').format('YYYY-MM-DD');
    const endOfMonth = dayjs().endOf('month').format('YYYY-MM-DD');
    return { startOfMonth, endOfMonth };
  };

  const { startOfMonth, endOfMonth } = getCurrentMonthDates();
  const [statistics, setStatistics] = React.useState<OrdersStatisticsType | null>(null);
  const [statisticsLoading, setStatisticsLoading] = React.useState(true);
  const [statisticsError, setStatisticsError] = React.useState<Error | null>(null);
  const [dateFrom, setDateFrom] = React.useState<string | null>(startOfMonth);
  const [dateTo, setDateTo] = React.useState<string | null>(endOfMonth);

  const loadStatistics = React.useCallback(async () => {
    setStatisticsError(null);
    setStatisticsLoading(true);

    try {
      const statsData = await getAllOrdersStatistics(dateFrom, dateTo);
      setStatistics(statsData);
    } catch (statsError) {
      setStatisticsError(statsError as Error);
    }
    setStatisticsLoading(false);
  }, [dateFrom, dateTo]);

  React.useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  const renderStatistics = React.useMemo(() => {
    // Show loading state
    if (statisticsLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: 500,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    // Show error state
    if (statisticsError) {
      return (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Alert severity="error">{statisticsError.message}</Alert>
        </Box>
      );
    }

    // Calculate total and percentages
    const totalCount = statistics
      ? statistics.counts.CREATED + statistics.counts.COMPLETED + statistics.counts.BOOKED + statistics.counts.CANCELED
      : 0;

    // If all counts are zero, show empty state
    if (totalCount === 0) {
      return (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              minHeight: 500,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              {t('orders.statistics')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              {t('common.noData')}
            </Typography>
          </Paper>
        </Box>
      );
    }

    // Calculate percentages
    const pendingPercentage = Math.round((statistics!.counts.CREATED / totalCount) * 100);
    const confirmedPercentage = Math.round(((statistics!.counts.BOOKED + statistics!.counts.COMPLETED) / totalCount) * 100);
    const cancelledPercentage = Math.round((statistics!.counts.CANCELED / totalCount) * 100);

    const data = [
      {
        name: t('employees.pendingRequests'),
        value: pendingPercentage,
        color: '#ff9800'
      },
      {
        name: t('employees.confirmedRequests'),
        value: confirmedPercentage,
        color: '#4caf50'
      },
      {
        name: t('employees.cancelledRequests'),
        value: cancelledPercentage,
        color: '#f44336'
      },
    ];

    const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
      const RADIAN = Math.PI / 180;
      // Increase the radius multiplier to add more margin (1.3 means 30% more distance)
      const radius = outerRadius * 1.3;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return (
        <text
          x={x}
          y={y}
          fill="#333"
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
          style={{ fontSize: '16px', fontWeight: 600 }}
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };

    return (
      <Box sx={{ width: '100%', mt: 1 }}>
        {/* Date Range Filter */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
          />
        </Box>

        {/* Total Orders Card */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.08) 0%, rgba(2, 139, 203, 0.03) 100%)',
            border: '2px solid',
            borderColor: '#028bcb',
            borderRadius: 2,
            textAlign: 'center',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(2, 139, 203, 0.2)',
              transform: 'translateY(-4px)',
            }
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              letterSpacing: 1.5,
              fontSize: '0.85rem',
            }}
          >
            {t('employees.totalOrders')}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mt: 1,
              fontWeight: 700,
              color: '#028bcb',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            {statistics?.total || 0}
          </Typography>
        </Paper>

        {/* Statistics Chart */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            minHeight: 500,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              fontWeight: 600,
              color: 'text.primary',
              textAlign: 'center',
            }}
          >
            {t('orders.statistics')}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => `${value}%`}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  padding: '10px',
                  direction: isRtl ? 'rtl' : 'ltr',
                  fontFamily: isRtl ? 'Cairo, sans-serif' : 'inherit',
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                wrapperStyle={{
                  direction: isRtl ? 'rtl' : 'ltr',
                  fontFamily: isRtl ? 'Cairo, sans-serif' : 'inherit',
                }}
                formatter={(value: string) => (
                  <span style={{ color: '#666', fontWeight: 500 }}>{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
    );
  }, [t, direction, statistics, statisticsLoading, statisticsError, dateFrom, dateTo]);

  return (
    <PageContainer
      breadcrumbs={[
        { title: t('orders.statistics') },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderStatistics}</Box>
    </PageContainer>
  );
}

